import _ from "lodash";
import appFeatures from "../../../appFeatures";
import type { GeodeMsalConfig} from "../../../authConfig";
import authConfig, {getActiveConfig} from "../../../authConfig";
import UsersApi from '@/uptk/core/api/UsersApi';
import { getClerkToken } from '@/uptk/core/api/clerkTokenProvider';
import { getFeaturesV3 } from '@/uptk/core/features';
import { getAuthContextState } from '@/uptk/core/api/auth_context_store';
import { syncSaasContext } from '@/uptk/core/api/saasContext';
import { get_default_auth_theme, normalize_auth_theme } from '@/uptk/core/theme/auth_theme';

const clerkJwtTemplate = import.meta.env.VITE_CLERK_JWT_TEMPLATE;

export class MsalSession {
    // Singleton instance used throughout this module.
    // This file uses `_instance` everywhere without null checks, so keep it non-nullable.
    static _instance: MsalSession;

    // Config (kept for legacy menu/feature wiring)
    msalInstance: any = null;
    // Proper config type for `getActiveConfig()`
    config: GeodeMsalConfig;

    // User profile shape used by the rest of this file
    profile: {
        photo: string | null;
        self: any;
        accessToken: string;
    } = {
        photo: null,
        self: {},
        accessToken: "",
    };

    theme: any = get_default_auth_theme();

    accountRoles: any;

    constructor(props?: unknown) {
        if (!MsalSession._instance) {
            MsalSession._instance = this;
        }
    }

    accounts: any[] = [];
    account: any = null;
    accountName: string | null = null;
    accessToken: string | null = null;
    roles: string[] = [];
    groups: string[] = [];
    workspaceId: string | null = null;
    workspaceIds: string[] = [];
    menus: string[] = [];
    routes: string[] = [];
    labels: string[] = [];
    auth: any = {};
    currentUser: any = null;

    reset = () => {
        this.accounts = [];
        this.account = null;
        this.accountName = null;
        this.accessToken = null;
        this.accountRoles = [];
        this.groups = [];
        this.workspaceId = null;
        this.workspaceIds = [];
        this.menus = [];
        this.routes = [];
        this.labels = [];
    }

    configure = async () => {

        if (!MsalSession._instance) {
            MsalSession._instance = this;
        }

        MsalSession._instance.config = getActiveConfig()

        MsalSession._instance.profile = {
            photo: null,
            self: {},
            accessToken: ""
        }
    }

    setTheme = (theme: any) => {
        if (!MsalSession._instance) {
            MsalSession._instance = this;
        }

        const normalizedTheme = normalize_auth_theme(theme);

        if (typeof localStorage !== 'undefined') {
            localStorage.setItem("theme", JSON.stringify(normalizedTheme))
        }

        const themeName = typeof normalizedTheme?.name === 'string' ? normalizedTheme.name : null
        const selectedTheme = themeName
            ? themeName
            : (typeof localStorage !== 'undefined' ? localStorage.getItem("selectedTheme") : null)

        this.theme = normalizedTheme
        MsalSession._instance.theme = normalizedTheme

        if (
            selectedTheme == null ||
            (typeof localStorage !== 'undefined' && selectedTheme == localStorage.getItem("selectedTheme"))
        ) {
            return;
        }
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem("selectedTheme", selectedTheme)
        }
    }

    getSession = () => {
        if (MsalSession == undefined) {
            return null
        }
        return MsalSession
    }

    getTheme = () => {
        const instance = MsalSession._instance ?? this
        const normalizedTheme = normalize_auth_theme(instance.theme)
        instance.theme = normalizedTheme
        return normalizedTheme
    }

    setTenantContext = (tenant?: string, fallback?: string) => {
        const candidate = [tenant, fallback, (typeof window !== 'undefined') ? localStorage.getItem('tenantid') : '']
            .find((val) => typeof val === 'string' && val.trim().length > 0) as string | undefined;
        if (candidate) {
            syncSaasContext({ tenantId: candidate.trim() });
        }
        return candidate;
    }

    getCurrentUserInfoFromBackend = async () => {

        if (!this.currentUser) {
            await UsersApi.getCurrentUserFromServer().then((user) => {
                if (user.data)  {
                    user = user.data;
                }
                this.currentUser = user;
                const tenantFromUser = (user as any)?.tenantid ?? (user as any)?.tenantId;
                const workspaceId = (user as any)?.workspaceId;
                this.setTenantContext(tenantFromUser, workspaceId);
            });
        }
        return this.currentUser;
    }

    currentAccount: any = () => {
        if (
            this.account != undefined
        ) {
            return (this.account)
        } else {
            return null
        }
    }

    getEmail = async () => {

        const currentUser = await this.getCurrentUserInfoFromBackend();
        if (currentUser) {
            return (currentUser as any).email;
        }
        return null;
    }

    isPermissionGrantedToUser = (user: any, entity: any, permission: any) => {

        if (!user || !user.groupRoles || !user.groupRoles.length) {
            return false;
        }

        // Check if any group is ADMIN or any role name contains admin
        const isAdminOrRoleAdmin = user.groupRoles.some((groupRole: any) => groupRole.group.name.toUpperCase() == "SYSTEM_ADMIN" ||
            groupRole.roles.some((role: any) => role.name.toUpperCase().includes("SYSTEM_ADMIN"))
        );

        // If user is an admin, automatically grant permission
        if (isAdminOrRoleAdmin) {
            return true;
        }

        // Iterate over user's groupRoles and their roles
        for (const groupRole of user.groupRoles) {
            for (const role of groupRole.roles) {
                for (const entityPermission of role.entityPermissions) {
                    // Check for specific entity and permission
                    if (entityPermission.entity == entity) {
                        if (entityPermission.permissions.includes(permission) || entityPermission.permissions.includes("ALL")) {
                            return true;
                        }
                    }
                }
            }
        }

        // Return false if no matching entity and permission are found
        return false;
    }

    // The MSAL accountRoles store and the legacy redux `role` selector are no
    // longer populated under Clerk + the dynamic shell, which silently hid every
    // admin-mode toggle (budget Duplicate/CloseYear, protected-field unlock —
    // board 05/08). The auth context (roles + groups) is the source of truth,
    // so both checks now also accept a matching auth-context role/group name.
    hasAuthContextRole = (role: any) => {
        const context = getAuthContextState() as { roles?: string[]; groups?: string[] } | null;
        if (!context) return false;
        const wanted = String(role ?? '').trim().toLowerCase();
        if (!wanted) return false;
        const names = [...(context.roles ?? []), ...(context.groups ?? [])]
            .map((name) => String(name).trim().toLowerCase());
        if (names.includes(wanted)) return true;
        // Legacy call sites ask for the "admins" group; accept the platform's
        // admin role spellings as equivalent.
        if (wanted === 'admins' || wanted === 'admin') {
            return names.includes('admin') || names.includes('admins') || names.includes('geode-admins-role');
        }
        return false;
    }

    checkAccountRole = (parent: any, role: any) => {
        const instance = MsalSession._instance;
        if (!instance) return false;
        if (_.isEmpty(instance.accountRoles)) {
            instance.getAccountRoles(parent);
        }
        if (instance.accountRoles != undefined && instance.accountRoles.includes(role)) {
            return true
        }
        return this.hasAuthContextRole(role)
    }

    checkCurrentAccountRole = (parent: any, role: any) => {
        const currentRole = parent.props.role;
        return currentRole == role || this.hasAuthContextRole(role)
    }

    //
    // Return the configured client id
    //
    clientId = () => {
        return MsalSession._instance?.config?.auth?.clientId ?? null
    }

    /**
     * Logins the user silently
     * Login the user using popup if the silent login fails
     * Login the user using redirect if the popup login fails
     * @returns
     */
    login = async () => {
        console.warn('login: MSAL login disabled; use Clerk SignInButton instead.');
    }

    /**
     * Login the user silently
     * @returns
     */
    loginSilent = async () => {
        console.warn('loginSilent: MSAL login disabled; use Clerk SignInButton instead.');
    }


    //
    // Login a user with a popup
    //
    loginUsingPopup = async () => {
        console.warn('loginUsingPopup: MSAL login disabled; use Clerk SignInButton instead.');
    }

    //
    // Login a user with a redirect
    //
    loginUsingRedirect = async () => {
        console.warn('loginUsingRedirect: MSAL login disabled; use Clerk SignInButton instead.');
    }

    // getSignedAccountName__MERGE = () => {
    //
    //     this.accounts = this.msalInstance.getAllAccounts();
    //     if (this.accounts.length < 1) { // No cached accounts
    //         this.accountName = null;
    //         return null;
    //     } else if (this.accounts.length > 1) { // Multiple account scenario
    //         // Add account selection code here
    //         const account = this.accounts[0];
    //         this.accountName = this.accounts[0].name;
    //         return this.accounts[0].name; // Multiple account scenario
    //
    //     } else if (this.accounts.length == 1) {
    //         const account = this.accounts[0];
    //         this.accountName = this.accounts[0].name;
    //         return this.accounts[0].name; // Single account scenario
    //     }
    // }

    getAppFeatureDefinition = (featureName: any) => {
        if (appFeatures == undefined) {
            return false;
        }
        const featurerec = _.filter(appFeatures, function (obj: any) {
            return (obj.name == featureName)
        })
        if (featurerec == undefined) {
            return false;
        }
        if (featurerec.length == 0) return false
        return featurerec
    }

    isAppFeatureDefinitionEnabled = (featureName: any) => {
        if (appFeatures == undefined) {
            return false;
        }
        const featurerec = _.filter(appFeatures, function (obj: any) {
            return (obj.name == featureName)
        })
        if (featurerec == undefined) {
            return false;
        }
        return featurerec[0].enabled
    }

    checkAppFeature = (featureName: any) => {
        const instance = MsalSession._instance;
        if (!instance?.config?.appFeatures) {
            return false;
        }
        const envFeatures = instance.config.appFeatures;
        /*
                if (appFeatures == undefined) {
                    return false;
                }
        */
        if (envFeatures.includes(featureName)) {
            return true;
        }
        return false
    }


    removeAppFeature = (featureName: any) => {
        if (MsalSession._instance?.config?.appFeatures) {
            _.remove(MsalSession._instance.config.appFeatures,
                function (currentObject: any) {
                    return currentObject == featureName;
                });
        }
    }

    getSignedAccountName = (caller: any) => {
        const storedName = localStorage.getItem('clerk_user_name');
        MsalSession._instance.accountName = storedName || null;
        return MsalSession._instance.accountName;
    }

    // getSignedAccount_MERGE = () => {
    //
    //     this.accounts = MsalSession._instance..msalInstance.getAllAccounts();
    //     if (this.accounts.length < 1) { // No cached accounts
    //         this.account = null;
    //         return null;
    //     } else if (this.accounts.length > 1) { // Multiple account scenario
    //         // Add account selection code here
    //         this.account = this.accounts[0];
    //         return this.accounts[0]; // Multiple account scenario
    //
    //     } else if (this.accounts.length == 1) {
    //         this.account = this.accounts[0];
    //         return this.accounts[0]; // Single account scenario
    //     }
    // }

    getSignedAccount = (parent: any) => {
        const storedId = localStorage.getItem('clerk_user_id');
        if (!storedId) {
            MsalSession._instance.account = null;
            return null;
        }
        MsalSession._instance.account = { id: storedId };
        return MsalSession._instance.account;
    }

    getAuth = () => {
        return MsalSession?._instance?.config?.auth
    }

    getAccountworkspaceId = (parent: any) => {
        return MsalSession._instance.workspaceId
    }

    getAccountworkspaceIds = async (parent: any) => {

        if (_.isEmpty(this.groups)) {
            await MsalSession._instance.getAccountGroupsAndworkspaceIds(parent, parent.props.selectedRole);
        }

        return MsalSession._instance.workspaceIds;
    }

    // TODO: Refactor this method
    getAccountGroupsAndworkspaceIds = async (parent: any, selectedRole: any) => {
        this.groups = ['*ALL'];

        // reset menus
        MsalSession._instance.menus = [];
        MsalSession._instance.labels = [];
        MsalSession._instance.routes = [];

        const authConfig = MsalSession._instance.config?.auth;
        if (!authConfig || !authConfig.menus) {
            return [];
        }

        const resolvedRole = selectedRole || localStorage.getItem('selectedRole') || 'users';
        const authMenus = authConfig.menus[resolvedRole] ?? authConfig.menus.users ?? [];
        const allowedGroups = MsalSession._instance.groups ?? [];
        const menus = _.filter(authMenus, (menu: any) => {
            const hasGroupAccess = Array.isArray(menu.groups)
                && (allowedGroups.includes('*ALL') || menu.groups.includes('*ALL')
                || menu.groups.some((group: string) => allowedGroups.includes(group)));
            return menu.selectable == true && (menu.protected == false || hasGroupAccess);
        });

        _.map(menus, (menu: any) => {
            if (!MsalSession._instance.menus.includes(menu.id)) {
                MsalSession._instance.menus.push(menu.id)
            }
        })

        const sortedMenus = _.map(menus, 'id')
        MsalSession._instance.menus = _.sortBy(MsalSession._instance.menus, function (item: any) {
            return sortedMenus.indexOf(item)
        });

        const newRoutes = _.map(menus, (menu: any) => {
            const menuId = menu.id.toLowerCase()
            if (menu.route != undefined) return menu.route
            return "/" + menuId
        });
        MsalSession._instance.routes = _.union(MsalSession._instance.routes, newRoutes);

        const newLabels = _.map(menus, (menu: any) => {
            const menuId = menu.id.toLowerCase()
            if (menu.label != undefined) return menu.label
            return 'menu.' + menuId
        });
        MsalSession._instance.labels = _.union(MsalSession._instance.labels, newLabels);

        const configuredWorkspaceIds = authConfig.workspaceIds ?? [];
        if (!_.isEmpty(configuredWorkspaceIds)) {
            MsalSession._instance.workspaceIds = _.union(this.workspaceIds, _.map(configuredWorkspaceIds, 'id'))
            MsalSession._instance.workspaceIds = _.sortedUniq(MsalSession._instance.workspaceIds)
            if (MsalSession._instance.workspaceId == null) {
                const authworkspaceId = _.filter(configuredWorkspaceIds, (workspaceId: any) => workspaceId.selected)
                if (!_.isEmpty(authworkspaceId)) {
                    MsalSession._instance.workspaceId = authworkspaceId[0].id
                }
            }
        }

        if (
            (MsalSession._instance.workspaceId == undefined || MsalSession._instance.workspaceId == null || MsalSession._instance.workspaceId.trim() == "") &&
            Array.isArray(configuredWorkspaceIds) && configuredWorkspaceIds.length > 0
        ) {
            MsalSession._instance.workspaceId = configuredWorkspaceIds[0].id
            parent.props.actions.saveworkspaceId(MsalSession._instance.workspaceId);
        }

        const workspaceIds = _.map(configuredWorkspaceIds, 'id');
        if (parent?.props?.actions?.saveworkspaceIds) {
            parent.props.actions.saveworkspaceIds(workspaceIds);
        }
        localStorage.getItem("workspaceId") == null && localStorage.setItem("workspaceId", MsalSession._instance.workspaceId ?? "")
        localStorage.getItem("workspaceId") == "null" && localStorage.setItem("workspaceId", MsalSession._instance.workspaceId ?? "")
        const workspaceId = localStorage.getItem("workspaceId")
        if (!_.isEmpty(workspaceIds) && workspaceId != null && workspaceIds.includes(workspaceId)) {
            parent.props.actions.saveworkspaceId(workspaceId);
        }

        console.log(
            '@@@ ALL AUTHs >> \n', {
                workspaceIds: MsalSession._instance.workspaceIds,
                workspaceId: MsalSession._instance.workspaceId,
                menus: MsalSession._instance.menus,
                routes: MsalSession._instance.routes,
                labels: MsalSession._instance.labels
            }
        )
        return MsalSession._instance.groups;
    }

    // TODO: Refactor this method
    loadAccountMenus = async () => {
        this.groups = []

        const features = getFeaturesV3(getAuthContextState(), ["finance"]);
        if (features.length > 0) {
            MsalSession._instance.menus.push("Finance");
            // APPEND instead of OVERWRITE
            MsalSession._instance.labels = _.union(MsalSession._instance.labels, ["menu.finance"]);
            MsalSession._instance.routes = _.union(MsalSession._instance.routes, ["/finance"]);
        }
        console.log(
            '@@@ ALL AUTHs >> \n', {
                menus: MsalSession._instance.menus,
                routes: MsalSession._instance.routes,
                labels: MsalSession._instance.labels
            }
        )
    }

    manageMsalLogin = async (parent: any) => {

        MsalSession._instance.account = MsalSession._instance.getSignedAccount(parent)
        if (
            parent.props != undefined &&
            parent.props.account != undefined &&
            MsalSession._instance.account != parent.props.account &&
            parent.props.actions != undefined &&
            parent.props.actions.saveAccount != undefined
        ) {
            parent.props.actions.saveAccount(MsalSession._instance.account)
        }

        MsalSession._instance.accountName = MsalSession._instance.getSignedAccountName(parent);

        // Refresh cached token if the configured Clerk template changes.
        const resolvedTemplate = clerkJwtTemplate?.trim();
        const cachedTemplate = localStorage.getItem('clerk_jwt_template');
        if (resolvedTemplate && cachedTemplate !== resolvedTemplate) {
            localStorage.removeItem('accessToken');
        }

        // get access token on first login
        let accessToken = localStorage.getItem('accessToken')
        if (MsalSession._instance.accountName && (!accessToken || accessToken == "null")) {
            MsalSession._instance.accessToken = await MsalSession._instance.getAccessToken()
            if (MsalSession._instance.accessToken) {
                localStorage.setItem('accessToken', MsalSession._instance.accessToken)
                if (resolvedTemplate) {
                    localStorage.setItem('clerk_jwt_template', resolvedTemplate);
                }
            }
        }

        const roles = MsalSession._instance.getAccountRoles(parent)

        const {role} = parent.props
        let activeRole = role
        if (Array.isArray(roles) && roles.length > 0 && role == null) {
            if (roles.includes("admins") && parent.props.role == null) {
                UsersApi.saveUserSelectRole("admins")
                await parent.props.actions.saveRole("admins");
                activeRole = "admins"
            } else if (roles.includes("users") && parent.props.role == null) {
                UsersApi.saveUserSelectRole("users")
                await parent.props.actions.saveRole("users");
                activeRole = "users"
            } else {
                if (parent.props.role == null) {
                    UsersApi.saveUserSelectRole(roles[0])
                    await parent.props.actions.saveRole(roles[0]);
                    activeRole = roles[0]
                }
            }
            if (parent.props.roles.length == 0) {
                await parent.props.actions.saveRoles(roles);
            }
        }

        if (activeRole) {
            await MsalSession._instance.getAccountGroupsAndworkspaceIds(parent, activeRole);
        }

        const accountWorkspaceIds = MsalSession._instance.getAccountworkspaceIds(parent)
        let selectedworkspaceId = MsalSession._instance.getAccountworkspaceId(parent)
        if (selectedworkspaceId == null) {
            if (Array.isArray(accountWorkspaceIds) && accountWorkspaceIds.length > 0) {
                selectedworkspaceId = accountWorkspaceIds[0]
            }
            localStorage.setItem("workspaceId", selectedworkspaceId ?? "")
            console.log('manageMsalLogin > saveworkspaceId : ' + selectedworkspaceId)
            parent.props.actions.saveworkspaceId(selectedworkspaceId);
            if (!_.isEqual(parent.props.workspaceIds, accountWorkspaceIds)) {
                parent.props.actions.saveworkspaceIds(accountWorkspaceIds);
            }
        }
    }

    getAccountRoles = (parent: any) => {
        let roles: string[] = [];
        const storedRoles = localStorage.getItem('clerk_roles');
        if (storedRoles) {
            try {
                const parsed = JSON.parse(storedRoles);
                if (Array.isArray(parsed)) {
                    roles = parsed.filter((role) => typeof role === 'string');
                }
            } catch (e) {
                // ignore invalid cache
            }
        }

        if (roles.length === 0) {
            const selectedRole = localStorage.getItem('selectedRole');
            if (selectedRole) {
                roles = [selectedRole];
            }
        }

        if (roles.length === 0) {
            roles = ['users'];
        }

        MsalSession._instance.accountRoles = roles;
        if (parent.props != undefined && parent.props.actions != undefined && parent.props.actions.saveRoles != undefined) {
            parent.props.actions.saveRoles(roles);
        }
        return roles;
    }

    isLoggedIn = () => {
        return localStorage.getItem('clerk_is_signed_in') === 'true';
    }

    isProfileAvailable = () => {
        return !!MsalSession._instance.profile
    }

    //
    // Set current user profile
    //
    setProfile = async () => {

        if (MsalSession._instance.profile == undefined) {
            MsalSession._instance.profile = {
                photo: null,
                self: {},
                accessToken: "",
            };
        }

        MsalSession._instance.profile.accessToken = await MsalSession._instance.getAccessToken()

        return MsalSession._instance.profile;

    }

    getProfile = () => {

        // Initialize
        if (!MsalSession._instance.profile.hasOwnProperty("photo")) {
            MsalSession._instance.profile.photo = null
        }
        if (!MsalSession._instance.profile.hasOwnProperty("self")) {
            MsalSession._instance.profile.self = {}
        }
        if (!MsalSession._instance.profile.hasOwnProperty("accessToken")) {
            MsalSession._instance.profile.accessToken = ""
        }

        // return profile
        return MsalSession._instance.profile;
    }

    //
    // Logout any stored user
    //
    logout = () => {
        this.reset();
        localStorage.removeItem('accessToken');
        localStorage.removeItem('clerk_is_signed_in');
        localStorage.removeItem('clerk_jwt_template');
    }

    //
    // Call to get user, probably cached and stored locally by MSAL
    //
    user = () => {
        return null
    }


    //
    // Call through to acquireTokenSilent or acquireTokenPopup
    //
    getAccessToken = async () => {
        const clerkToken = await getClerkToken();
        if (clerkToken) {
            return clerkToken;
        }

        console.warn('getAccessToken: Clerk token not available. Ensure Clerk is configured.');
        return "";
     }

    //
    // Clear any stored/cached user
    //
    clearLocal = () => {
        for (let entry of Object.entries(localStorage)) {
            let key = entry[0]
            if (key.includes('login.windows')) {
                localStorage.removeItem(key)
            }
        }
    }

    getInstance = () => {
        return { config: MsalSession._instance.config }
    }

    getConfig = () => {
        return MsalSession._instance.config
    }

    loginComplete = async () => {
        // MsalSession._instance.setState({ user: auth.user() })
        await this.setProfile()
        await this.getCurrentUserInfoFromBackend();

    }

    shallowLogout = (me: any) => {
        me.setState({
            user: null,
            accessToken: null,
            graphDetails: null,
            graphPhoto: null,
        })
        MsalSession._instance.clearLocal()
    }
}




